import { Sparkles, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Message } from '../../types';
import { PreviewRenderer } from '../previews/PreviewRenderer';
import { ThinkingPanel } from './ThinkingPanel';

interface ChatMessageProps {
  message: Message;
  /** Only rendered for USER messages — deletes this question and its response as one turn. */
  onDelete?: () => void;
}

export function ChatMessage({ message, onDelete }: ChatMessageProps) {
  // Hide TOOL and SYSTEM messages
  if (message.role === 'TOOL' || message.role === 'SYSTEM') {
    return null;
  }

  // A tool-calling turn (with or without preamble narration like "Let me check that...") is
  // persisted for conversation-history purposes but isn't a real answer — extended thinking
  // makes the model narrate between tool calls far more often, and rendering each one as its
  // own bubble reads as the assistant giving several disjointed replies to a single prompt.
  // Only the turn with no tool call (the actual final answer) should render.
  if (message.role === 'ASSISTANT' && (message.hasToolCalls || !message.content || message.content.trim() === '')) {
    return null;
  }

  const isUser = message.role === 'USER';

  return (
    <div
      data-testid="chat-message"
      data-role={message.role}
      className={`group flex ${isUser ? 'justify-end' : 'justify-start'} gap-2`}
    >
      {/* Assistant avatar */}
      {!isUser && (
        <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center shrink-0 mt-1">
          <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
      )}

      {/* Delete — only on the user's own questions, only once it's a real persisted message
          (the optimistic pending bubble and the ephemeral "hey" have no onDelete handler). */}
      {isUser && onDelete && (
        <button
          onClick={onDelete}
          aria-label="Delete this question"
          data-testid="delete-message-button"
          className="self-center shrink-0 h-6 w-6 flex items-center justify-center rounded text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}

      <div className={`max-w-[80%] ${isUser ? '' : ''}`}>
        {!isUser && message.reasoningContent && (
          <ThinkingPanel steps={[{ type: 'thinking', text: message.reasoningContent }]} />
        )}
        <div
          className={`rounded-lg px-3 py-2 text-sm ${
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground'
          }`}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap break-words">{message.content}</p>
          ) : (
            <MarkdownContent content={message.content} />
          )}
          <p
            className={`text-[10px] mt-1 ${
              isUser ? 'text-primary-foreground/60' : 'text-muted-foreground'
            }`}
          >
            {format(new Date(message.createdAt), 'h:mm a')}
          </p>
        </div>

        {message.previewType && message.previewData && (
          <div className="mt-2">
            <PreviewRenderer
              previewType={message.previewType}
              data={message.previewData}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Renders assistant markdown (bold, lists, headings, code, links, tables) instead of the
 * raw asterisks/backticks the model produces — those were showing up literally in the chat.
 * Only used for assistant messages; user messages are rendered as plain text.
 */
export function MarkdownContent({ content }: { content: string }) {
  return (
    <div className="break-words [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="whitespace-pre-wrap mb-2">{children}</p>,
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-0.5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5">{children}</ol>,
          li: ({ children }) => <li>{children}</li>,
          h1: ({ children }) => <h1 className="text-base font-semibold mt-2 mb-1">{children}</h1>,
          h2: ({ children }) => <h2 className="text-sm font-semibold mt-2 mb-1">{children}</h2>,
          h3: ({ children }) => <h3 className="text-sm font-semibold mt-2 mb-1">{children}</h3>,
          a: ({ children, href }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:opacity-80">
              {children}
            </a>
          ),
          code: ({ children, className }) => {
            const isBlock = /language-/.test(className || '');
            return isBlock ? (
              <code className={className}>{children}</code>
            ) : (
              <code className="rounded bg-black/10 px-1 py-0.5 text-[0.85em] font-mono">{children}</code>
            );
          },
          pre: ({ children }) => (
            <pre className="rounded-md bg-black/10 p-2 mb-2 overflow-x-auto text-[0.85em] font-mono">{children}</pre>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-current/30 pl-2 italic opacity-90 mb-2">{children}</blockquote>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto mb-2">
              <table className="border-collapse text-sm">{children}</table>
            </div>
          ),
          th: ({ children }) => <th className="border border-current/20 px-2 py-1 text-left font-semibold">{children}</th>,
          td: ({ children }) => <td className="border border-current/20 px-2 py-1">{children}</td>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
